import { z } from 'zod';
import type {
  ApiSchemaClass,
  ApiSchemaDefinition,
  ApiSchemaMapperDefinition,
  ApiSchemaMapperClass,
} from './types';
import { uiMessageArraySchema, uiMessageFieldsSchema } from './ui-message';

export const chatMessageTypeSchema = z.enum(['chat', 'hidden', 'compaction']);
export type ChatMessageType = z.infer<typeof chatMessageTypeSchema>;

export const chatTriggerSourceSchema = z.enum([
  'chat',
  'chat-bot',
  'slack',
  'linear',
  'webhook',
  'warehouse',
  'cron',
  'event',
  'dataset',
  'compaction-summary',
  'workflow',
  'system',
  'api',
]);

export const chatPostContextSchema = z.object({
  systemSuffix: z.string().optional(),
  toolIds: z.array(z.string()),
  orgId: z.string(),
  // Optional: only sent when the user explicitly chose a model this session.
  // When omitted, the server keeps the conversation's persisted model so a
  // follow-up (e.g. on an agent-run chat) doesn't clobber the agent's model.
  model: z.string().optional(),
  agent: z.string().nullable().optional(),
  agentId: z.string().optional(),
  mode: z.string(),
  planMode: z.boolean().optional(),
  folderId: z.string().optional(),
  trigger: chatTriggerSourceSchema.optional(),
  /**
   * Opt-in: when true and the request's `messages` include a `role: 'system'`
   * message, that system text replaces the default (coco) system prompt on a
   * new chat instead of being prepended as an extra system message. No-op on
   * existing chats, saved-agent chats, and when no system message is supplied.
   */
  overrideSystemPrompt: z.boolean().optional(),
});

export type ChatTriggerSource = z.infer<typeof chatTriggerSourceSchema>;

/**
 * The chats a person would call theirs: conversations someone started, as
 * opposed to the runs agents and workflows stamp with their own source.
 *
 * Both the default filter for a chat history list and the definition of which
 * chats a folder surfaces in its collapsed "Chats" row. Shared, because the
 * count the server reports for a folder and the list that row opens have to
 * agree — counting the runs made a folder report 61,052 items over a list of 9,
 * and counting them in the folder payload while the row filtered them out would
 * simply move the same contradiction one step along.
 */
export const DEFAULT_CHAT_HISTORY_TRIGGER_SOURCES: ChatTriggerSource[] = [
  'chat',
];

export type ChatPostContext = z.infer<typeof chatPostContextSchema>;

export const chatPostMessageV20260601Schema = uiMessageFieldsSchema.extend({
  messageType: chatMessageTypeSchema
    .optional()
    .describe('Visibility: chat (default), hidden, or compaction summary.'),
});

export type ChatPostMessageV20260601 = z.infer<
  typeof chatPostMessageV20260601Schema
>;

export const chatPostInitialActionSchema = z
  .object({
    type: z.string(),
    shape: z.unknown().optional(),
  })
  .catchall(z.unknown());

/**
 * Ad-hoc client-implemented (interruptable) tool definition. Calling such a
 * tool suspends the run with an incomplete tool call; the client executes it
 * and completes via `POST :id/tools/:toolCallId/run-interrupted`.
 */
export const chatPostInterruptableToolSchema = z.object({
  toolKey: z
    .string()
    .regex(
      /^client\/[a-z0-9][a-z0-9-]*(\/[a-zA-Z0-9][a-zA-Z0-9-]*)*$/,
      'toolKey must be namespaced under "client/" (e.g. client/save-artifacts)'
    ),
  name: z.string().min(1),
  description: z.string().min(1),
  /** JSON schema describing the tool input. */
  inputSchema: z.record(z.string(), z.unknown()),
});

export type ChatPostInterruptableTool = z.infer<
  typeof chatPostInterruptableToolSchema
>;

export const chatPostBodyV20260601Schema = z.object({
  context: chatPostContextSchema,
  messages: z.array(chatPostMessageV20260601Schema),
  initialArtifacts: z.array(z.unknown()).optional(),
  initialToolKeys: z.array(z.string()).optional(),
  initialActions: z.array(chatPostInitialActionSchema).optional(),
  interruptableTools: z.array(chatPostInterruptableToolSchema).optional(),
});

export type ChatPostBodyV20260601 = z.infer<typeof chatPostBodyV20260601Schema>;

export const chatPostBodyLegacySchema = z.object({
  context: chatPostContextSchema,
  messages: uiMessageArraySchema,
  messageTypeById: z.record(z.string(), chatMessageTypeSchema).optional(),
  initialArtifacts: z.array(z.unknown()).optional(),
  initialToolKeys: z.array(z.string()).optional(),
  initialActions: z.array(chatPostInitialActionSchema).optional(),
  interruptableTools: z.array(chatPostInterruptableToolSchema).optional(),
});

export type ChatPostBodyLegacy = z.infer<typeof chatPostBodyLegacySchema>;

export const chatPostResponseSchema = z.object({
  success: z.boolean(),
});

export type ChatPostResponse = z.infer<typeof chatPostResponseSchema>;

export class ChatPostLegacySchema implements ApiSchemaDefinition {
  static readonly version = 'chat-legacy' as const;

  readonly name = ChatPostLegacySchema.version;
  readonly schema = chatPostBodyLegacySchema;
  readonly response = chatPostResponseSchema;
}

export class ChatPostV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'chat-2026-06-01' as const;

  readonly name = ChatPostV20260601Schema.version;
  readonly schema = chatPostBodyV20260601Schema;
  readonly response = chatPostResponseSchema;

  static mappers(): ApiSchemaMapperClass[] {
    return [ChatLegacyToV20260601Mapper];
  }
}

export class ChatLegacyToV20260601Mapper implements ApiSchemaMapperDefinition {
  readonly fromVersion = ChatPostLegacySchema.version;
  readonly toVersion = ChatPostV20260601Schema.version;

  body(body: ChatPostBodyLegacy): ChatPostBodyV20260601 {
    const { messageTypeById, messages, ...rest } = body;

    return {
      ...rest,
      messages: messages.map((message) => ({
        ...message,
        messageType:
          messageTypeById?.[message.id ?? ''] ?? ('chat' as ChatMessageType),
      })),
    };
  }
}

export const CHAT_API_SCHEMA_VERSIONS = {
  legacy: ChatPostLegacySchema.version,
  v20260601: ChatPostV20260601Schema.version,
} as const;

// ---------------------------------------------------------------------------
// POST :id/messages/read
// ---------------------------------------------------------------------------

export const chatMessagesReadBodySchema = z.object({
  messageIds: z.array(z.string()).optional(),
});

export type ChatMessagesReadBody = z.infer<typeof chatMessagesReadBodySchema>;

export const chatSuccessResponseSchema = z.object({
  success: z.boolean(),
});

export type ChatSuccessResponse = z.infer<typeof chatSuccessResponseSchema>;

export class ChatMessagesReadV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'chat-messages-read-2026-06-01' as const;

  readonly name = ChatMessagesReadV20260601Schema.version;
  readonly schema = chatMessagesReadBodySchema;
  readonly response = chatSuccessResponseSchema;
}

// ---------------------------------------------------------------------------
// POST :id/messages/unread
// ---------------------------------------------------------------------------

export const chatMessagesUnreadBodySchema = z.object({
  // Optional, mirroring the read endpoint: omit to mark every assistant message
  // unread (how the sidebar's "Mark as unread" acts, knowing only the chat id).
  messageIds: z.array(z.string()).optional(),
});

export type ChatMessagesUnreadBody = z.infer<
  typeof chatMessagesUnreadBodySchema
>;

export class ChatMessagesUnreadV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'chat-messages-unread-2026-06-01' as const;

  readonly name = ChatMessagesUnreadV20260601Schema.version;
  readonly schema = chatMessagesUnreadBodySchema;
  readonly response = chatSuccessResponseSchema;
}

// ---------------------------------------------------------------------------
// POST :id/rename
// ---------------------------------------------------------------------------

export const chatRenameBodySchema = z.object({
  title: z.string().min(1),
});

export type ChatRenameBody = z.infer<typeof chatRenameBodySchema>;

export class ChatRenameV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'chat-rename-2026-06-01' as const;

  readonly name = ChatRenameV20260601Schema.version;
  readonly schema = chatRenameBodySchema;
  readonly response = chatSuccessResponseSchema;
}

// ---------------------------------------------------------------------------
// POST :id/actions
// ---------------------------------------------------------------------------

export const chatAddActionSchema = z.object({
  type: z.string(),
  agentId: z.string().optional(),
  artifactType: z.string().optional(),
  artifactId: z.string().optional(),
  data: z.unknown().optional(),
  toolKey: z.string().optional(),
  messageId: z.string().optional(),
});

export type ChatAddAction = z.infer<typeof chatAddActionSchema>;

export const chatActionsBodySchema = z.object({
  action: chatAddActionSchema,
});

export type ChatActionsBody = z.infer<typeof chatActionsBodySchema>;

export class ChatActionsV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'chat-actions-2026-06-01' as const;

  readonly name = ChatActionsV20260601Schema.version;
  readonly schema = chatActionsBodySchema;
  readonly response = chatSuccessResponseSchema;
}

// ---------------------------------------------------------------------------
// POST :id/tools/:toolCallId/run
// ---------------------------------------------------------------------------

export const chatToolRunActionSchema = z.enum(['approve', 'deny']);

export const chatToolRunBodySchema = z.object({
  model: z.string(),
  action: chatToolRunActionSchema,
  reason: z.string().optional(),
  /** When true, persist approve/deny only — caller will start the next ATC run. */
  skipResume: z.boolean().optional(),
});

export type ChatToolRunBody = z.infer<typeof chatToolRunBodySchema>;

export const chatToolRunResponseSchema = z.object({
  success: z.boolean(),
  resumed: z.boolean(),
});

export type ChatToolRunResponse = z.infer<typeof chatToolRunResponseSchema>;

export class ChatToolRunV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'chat-tool-run-2026-06-01' as const;

  readonly name = ChatToolRunV20260601Schema.version;
  readonly schema = chatToolRunBodySchema;
  readonly response = chatToolRunResponseSchema;
}

// ---------------------------------------------------------------------------
// POST :id/tools/:toolCallId/run-interrupted
// ---------------------------------------------------------------------------

export const chatToolRunInterruptedBodySchema = z.object({
  model: z.string(),
  input: z.unknown(),
});

export type ChatToolRunInterruptedBody = z.infer<
  typeof chatToolRunInterruptedBodySchema
>;

export class ChatToolRunInterruptedV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'chat-tool-run-interrupted-2026-06-01' as const;

  readonly name = ChatToolRunInterruptedV20260601Schema.version;
  readonly schema = chatToolRunInterruptedBodySchema;
  readonly response = chatToolRunResponseSchema;
}

// ---------------------------------------------------------------------------
// POST :id/interruptable-tools
// ---------------------------------------------------------------------------

/**
 * Declares client-implemented tools on an existing conversation, mid-run.
 *
 * The chat POST body carries the same shape, but only a new message can use it;
 * this is for tools the client discovers while the model is already working —
 * a frontend-action search answering what the open page can do — which have to
 * become callable without waiting for the user to say something.
 */
export const chatAddInterruptableToolsBodySchema = z.object({
  tools: z.array(chatPostInterruptableToolSchema),
});

export type ChatAddInterruptableToolsBody = z.infer<
  typeof chatAddInterruptableToolsBodySchema
>;

export const chatAddInterruptableToolsResponseSchema = z.object({
  success: z.boolean(),
  /** Tool keys now callable on this conversation. */
  toolKeys: z.array(z.string()),
});

export type ChatAddInterruptableToolsResponse = z.infer<
  typeof chatAddInterruptableToolsResponseSchema
>;

export class ChatAddInterruptableToolsV20260601Schema
  implements ApiSchemaDefinition
{
  static readonly version = 'chat-add-interruptable-tools-2026-06-01' as const;

  readonly name = ChatAddInterruptableToolsV20260601Schema.version;
  readonly schema = chatAddInterruptableToolsBodySchema;
  readonly response = chatAddInterruptableToolsResponseSchema;
}

// ---------------------------------------------------------------------------
// POST :id/tools/submit
// ---------------------------------------------------------------------------

export const chatToolSubmitBodySchema = z.object({
  toolCallIds: z.array(z.string()),
  model: z.string(),
  result: z.unknown(),
});

export type ChatToolSubmitBody = z.infer<typeof chatToolSubmitBodySchema>;

export const chatToolSubmitResponseSchema = z.object({
  success: z.boolean(),
  conversationId: z.string(),
  resumed: z.boolean(),
});

export type ChatToolSubmitResponse = z.infer<
  typeof chatToolSubmitResponseSchema
>;

export class ChatToolSubmitV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'chat-tool-submit-2026-06-01' as const;

  readonly name = ChatToolSubmitV20260601Schema.version;
  readonly schema = chatToolSubmitBodySchema;
  readonly response = chatToolSubmitResponseSchema;
}

// ---------------------------------------------------------------------------
// POST :id/compact
// ---------------------------------------------------------------------------

export const chatCompactBodySchema = z.object({
  model: z.string(),
});

export type ChatCompactBody = z.infer<typeof chatCompactBodySchema>;

export class ChatCompactV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'chat-compact-2026-06-01' as const;

  readonly name = ChatCompactV20260601Schema.version;
  readonly schema = chatCompactBodySchema;
  readonly response = chatSuccessResponseSchema;
}

// ---------------------------------------------------------------------------
// POST :id/step
// ---------------------------------------------------------------------------

/**
 * Runs a single `stepChat` frame (one LLM turn) after appending the request's
 * messages, instead of the full agent loop. Same request body as the chat POST;
 * the difference is behavioral: no pending-tool execution or `extractObject`
 * second pass follows the turn.
 */
export class ChatStepV20260601Schema implements ApiSchemaDefinition {
  static readonly version = 'chat-step-2026-06-01' as const;

  readonly name = ChatStepV20260601Schema.version;
  readonly schema = chatPostBodyV20260601Schema;
  readonly response = chatPostResponseSchema;
}

const jsonValueSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(z.string(), jsonValueSchema),
  ])
);

const chatInsightsToolCallSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  messageId: z.string(),
  position: z.number(),
  state: z.string(),
  input: jsonValueSchema,
  output: jsonValueSchema,
});

const chatInsightsTurnSchema = z.object({
  turnIndex: z.number(),
  userMessageId: z.string().nullable(),
  userMessagePosition: z.number().nullable(),
  toolCalls: z.array(chatInsightsToolCallSchema),
});

const chatInsightsResultSchema = z.object({
  messageId: z.string(),
  position: z.number(),
  output: jsonValueSchema,
  text: z.string().nullable(),
});

const chatInsightsArtifactSchema = z.object({
  type: z.string(),
  artifactId: z.string(),
  payload: jsonValueSchema,
  messageId: z.string(),
  toolCallId: z.string(),
  toolName: z.string(),
});

const chatInsightsSentimentSchema = z.object({
  label: z.string().nullable(),
  score: z.number().nullable(),
  intent: z.string().nullable(),
  summary: z.string().nullable(),
  completionStatus: z.string().nullable(),
  completionSummary: z.string().nullable(),
  observedAt: z.string().nullable(),
  analysisId: z.string().nullable(),
});

const chatInsightsCompactionSchema = z.object({
  messageId: z.string(),
  position: z.number(),
  createdAt: z.string(),
  content: z.string(),
});

const chatInsightsFactSchema = z.object({
  scope: z.string(),
  text: z.string(),
});

const chatInsightsCountsSchema = z.object({
  userMessages: z.number(),
  assistantMessages: z.number(),
  toolCalls: z.number(),
});

const chatInsightsAssistantMessageUsageSchema = z.object({
  resultCacheHit: z.boolean(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  cachedInputTokens: z.number(),
});

const chatInsightsMetadataSchema = z.object({
  triggerSource: chatTriggerSourceSchema,
  agentId: z.string().nullable(),
  parentId: z.string().nullable(),
  model: z.string().nullable(),
});

export type ChatInsightsAssistantMessageUsage = z.infer<
  typeof chatInsightsAssistantMessageUsageSchema
>;

export type ChatInsightsResponse = {
  conversationId: string;
  parentToolCallId: string | null;
  facts: z.infer<typeof chatInsightsFactSchema>[];
  turns: z.infer<typeof chatInsightsTurnSchema>[];
  result: z.infer<typeof chatInsightsResultSchema> | null;
  artifacts: z.infer<typeof chatInsightsArtifactSchema>[];
  sentiment: z.infer<typeof chatInsightsSentimentSchema> | null;
  compactions: z.infer<typeof chatInsightsCompactionSchema>[];
  counts: z.infer<typeof chatInsightsCountsSchema>;
  metadata: z.infer<typeof chatInsightsMetadataSchema>;
  llmUsageByMessageId: Record<string, ChatInsightsAssistantMessageUsage>;
  subAgents: ChatInsightsResponse[];
};

export const chatInsightsResponseSchema: z.ZodType<ChatInsightsResponse> =
  z.lazy(() =>
    z.object({
      conversationId: z.string(),
      parentToolCallId: z.string().nullable(),
      facts: z.array(chatInsightsFactSchema),
      turns: z.array(chatInsightsTurnSchema),
      result: chatInsightsResultSchema.nullable(),
      artifacts: z.array(chatInsightsArtifactSchema),
      sentiment: chatInsightsSentimentSchema.nullable(),
      compactions: z.array(chatInsightsCompactionSchema),
      counts: chatInsightsCountsSchema,
      metadata: chatInsightsMetadataSchema,
      llmUsageByMessageId: z.record(
        z.string(),
        chatInsightsAssistantMessageUsageSchema
      ),
      subAgents: z.array(chatInsightsResponseSchema),
    })
  );

export class ChatInsightsV20260701Schema implements ApiSchemaDefinition {
  static readonly version = 'chat-insights-2026-07-01' as const;

  readonly name = ChatInsightsV20260701Schema.version;
  readonly response = chatInsightsResponseSchema;
}

export const CHAT_API_SCHEMAS = [
  ChatPostLegacySchema,
  ChatPostV20260601Schema,
  ChatMessagesReadV20260601Schema,
  ChatMessagesUnreadV20260601Schema,
  ChatRenameV20260601Schema,
  ChatActionsV20260601Schema,
  ChatToolRunV20260601Schema,
  ChatToolRunInterruptedV20260601Schema,
  ChatToolSubmitV20260601Schema,
  ChatCompactV20260601Schema,
  ChatStepV20260601Schema,
  ChatInsightsV20260701Schema,
] as const satisfies readonly ApiSchemaClass[];
